import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	listArchivedPropertyContactsHousesHouseIdContactsArchivedGetOptions,
	listArchivedPropertyContactsHousesHouseIdContactsArchivedGetQueryKey,
	listContactCandidatesHousesHouseIdContactsCandidatesGetQueryKey,
	listDiscardedContactCandidatesHousesHouseIdContactsCandidatesDiscardedGetOptions,
	listDiscardedContactCandidatesHousesHouseIdContactsCandidatesDiscardedGetQueryKey,
	listPropertyContactsHousesHouseIdContactsGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import { ProfessionalContacts } from '@/client';
import { toaster } from '@/components/ui/toaster';
import { ArchiveEntryKinds, type ArchiveEntry } from '@/components/services/model/contactArchive';
import { usePlanRefresh } from '@/hooks/maintenance/usePlanRefresh';

/**
 * The two lists behind the Archive tab, and the two ways back out of it.
 *
 * They are separate endpoints because a restore lands in a different place for each: an archived
 * `property_contacts` row returns to the contacts list, and a discarded `extracted_contacts` row
 * returns to the review queue as pending. One endpoint could not do both.
 */
export const archiveQueryKeys = (houseId: number) => ({
	archived: listArchivedPropertyContactsHousesHouseIdContactsArchivedGetQueryKey({
		path: { house_id: houseId },
	}),
	discarded: listDiscardedContactCandidatesHousesHouseIdContactsCandidatesDiscardedGetQueryKey({
		path: { house_id: houseId },
	}),
});

/**
 * Contacts archived at this property, most recently archived first.
 *
 * Ordered by `updated_at`, which has no `archived_at` behind it — a correction moves an entry up
 * the list as an archiving would. Close enough to order a restore list by, and worth knowing before
 * anybody reads the order as a history.
 *
 * Gated on HOUSE_NETWORK_MANAGE by the endpoint: the only thing to do here is restore.
 */
export const useArchivedContacts = (houseId: number, enabled: boolean) =>
	useQuery({
		...listArchivedPropertyContactsHousesHouseIdContactsArchivedGetOptions({ path: { house_id: houseId } }),
		enabled: enabled && !!houseId,
	});

/**
 * Candidates this household rejected, ordered by name.
 *
 * Not by recency: `processing_status` carries no timestamp, so there is nothing to order by.
 *
 * Only the household's own rejections. A candidate the reconciler filtered out is absent — nobody
 * decided it, and no endpoint could put it back, because the reconciler re-derives `excluded` on
 * every run.
 */
export const useDiscardedCandidates = (houseId: number, enabled: boolean) =>
	useQuery({
		...listDiscardedContactCandidatesHousesHouseIdContactsCandidatesDiscardedGetOptions({
			path: { house_id: houseId },
		}),
		enabled: enabled && !!houseId,
	});

/**
 * Puts an archived contact back on the property's list.
 *
 * It returns **disconnected**: restore never grants house access, so a contact that could see the
 * property's documents before it was archived cannot after. Connecting again is a separate act.
 * The household's `override_*` corrections do come back — archiving never cleared them.
 */
export const useRestoreContact = (houseId: number) => {
	const queryClient = useQueryClient();
	const keys = archiveQueryKeys(houseId);
	const refreshPlan = usePlanRefresh(houseId);

	return useMutation({
		mutationFn: (propertyContactId: number) =>
			ProfessionalContacts.restoreContactToPropertyHousesHouseIdContactsPropertyContactIdRestorePost({
				path: { house_id: houseId, property_contact_id: propertyContactId },
				throwOnError: true,
			}).then(({ data }) => data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys.archived });
			void queryClient.invalidateQueries({
				queryKey: listPropertyContactsHousesHouseIdContactsGetQueryKey({ path: { house_id: houseId } }),
			});
			// A restored saved contact can become the plan card's provider again.
			refreshPlan();
		},
		onError: (error: Error) => {
			toaster.create({ title: 'Could not restore this contact', description: error.message, type: 'error' });
		},
	});
};

/**
 * Puts a discarded candidate back on the review queue as pending.
 *
 * Separate from `useContactCandidateActions.undo`, which is the way back from a discard the reader
 * has just made and keeps the review pass's own optimistic list in step. This one is read from the
 * archive, where that list is not on screen.
 */
export const useRestoreCandidate = (houseId: number) => {
	const queryClient = useQueryClient();
	const keys = archiveQueryKeys(houseId);
	const refreshPlan = usePlanRefresh(houseId);

	return useMutation({
		mutationFn: (candidateId: number) =>
			ProfessionalContacts.undoContactDecisionHousesHouseIdContactsCandidatesExtractedContactIdUndoPost({
				path: { house_id: houseId, extracted_contact_id: candidateId },
				throwOnError: true,
			}).then(({ data }) => data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys.discarded });
			void queryClient.invalidateQueries({
				queryKey: listContactCandidatesHousesHouseIdContactsCandidatesGetQueryKey({
					path: { house_id: houseId },
				}),
			});
			// The plan carries the candidate lifecycle independently of the review queue.
			refreshPlan();
		},
		onError: (error: Error) => {
			toaster.create({ title: 'Could not restore this suggestion', description: error.message, type: 'error' });
		},
	});
};

/**
 * Restores an archive entry by whichever path its kind needs, and says which entry is on its way.
 *
 * One home for the rule because the Archive draws two kinds of recoverable row, each restored by a
 * different endpoint.
 */
export const useRestoreArchiveEntry = (houseId: number) => {
	const restoreContact = useRestoreContact(houseId);
	const restoreCandidate = useRestoreCandidate(houseId);
	const mutationFor = (entry: ArchiveEntry) =>
		entry.kind === ArchiveEntryKinds.Contact ? restoreContact : restoreCandidate;

	return {
		restore: (entry: ArchiveEntry) => mutationFor(entry).mutate(entry.id),
		isRestoring: (entry: ArchiveEntry) => {
			const mutation = mutationFor(entry);
			return mutation.isPending && mutation.variables === entry.id;
		},
	};
};
